import React from 'react';
import { Button } from 'antd';
import { TEligibilityCheckVo } from 'types/insuranceEligibility';
import { FOOTER_COPY, REJECTED_COPY } from '../copy';
import { FIX_FIELD_LABELS, TAaaEntry, TMergedRejection, aaaForAction, opsRef } from '../aaa';
import LastCheckedLine from './LastCheckedLine';
import BillingFooter from './BillingFooter';
import AwaitStrip from './AwaitStrip';
import s from '../s.module.less';

type Props = {
    check: TEligibilityCheckVo;
    merged: TMergedRejection;
    persona: 'managed' | 'manual';
    skipCostModal: boolean;
    clientId: number;
    memberIdOnFile?: string;
    // 已有未满足的卡片请求：card 按钮隐藏，awaiting 条顶上
    cardRequestedAt?: string;
    // 二次失败（原型 fixLine）：整句替换 Recommended fix——「改正 X 然后 re-check」第一次是对的，
    // 系统已自动让患者改过并复查过之后就是假话；多码合并的 combinedFix 同样整体替换
    secondFailFix?: string;
    onRequestCard: (aaa?: TAaaEntry) => void;
    onMessagePatient: (aaa?: TAaaEntry) => void;
    // 「查另一张」盒（10/05，not found 类拒识时由容器传入）
    extra?: React.ReactNode;
    planScope?: string;
};

// AAA 拒识修复卡 body：chip 报问题（bar 承担）、exp 消误读、fix 盒说怎么办。
// 多码时只有 lead bucket 出按钮，deferred 一行灰字（不隐藏、不出第二组按钮）
const RejectedBody: React.FC<Props> = ({
    check,
    merged,
    persona,
    skipCostModal,
    clientId,
    memberIdOnFile,
    cardRequestedAt,
    secondFailFix,
    onRequestCard,
    onMessagePatient,
    extra,
    planScope,
}) => {
    const a = merged.aaas[0];
    const { multi } = merged;
    // 单码细节（on-file 值、字段名）在多码时无意义，直接丢弃而非任选一个
    const fieldLabel = !multi && a.fixField ? FIX_FIELD_LABELS[a.fixField] : undefined;
    const currentValue = !multi && a.fixField === 'memberId' ? memberIdOnFile : undefined;
    // 请求已发出后 card 按钮撤下（避免患者被要两次卡），位置由 awaiting 条接替
    const actions = merged.actions.filter((act) => !(act === 'card' && cardRequestedAt));

    return (
        <>
            <LastCheckedLine checkedAt={check.checkedAt} checkedBy={check.checkedBy} planScope={planScope} />
            {a.fixField ? (
                <div className={s.infoLine}>{REJECTED_COPY.exp}</div>
            ) : (
                <div className={s.infoLine}>{a.plain}</div>
            )}
            {a.fixField ? (
                <div className={s.fixBox}>
                    <b>{REJECTED_COPY.recommendedFix}</b> {secondFailFix || merged.combinedFix}
                    {fieldLabel && currentValue && (
                        <div className={s.fixOnFile}>
                            {REJECTED_COPY.onFile(fieldLabel)} <code>{currentValue}</code>
                        </div>
                    )}
                    {actions.length > 0 && (
                        <div className={s.fixActions}>
                            {actions.includes('card') && (
                                <Button type="primary" onClick={() => onRequestCard(aaaForAction(merged, 'card'))}>
                                    {REJECTED_COPY.requestCardBtn}
                                </Button>
                            )}
                            {actions.includes('message') && (
                                <Button
                                    type={actions.length === 1 ? 'primary' : 'default'}
                                    onClick={() => onMessagePatient(aaaForAction(merged, 'message'))}
                                >
                                    {REJECTED_COPY.messagePatientBtn}
                                </Button>
                            )}
                        </div>
                    )}
                    {merged.deferred.length > 0 && (
                        <div className={s.fixDeferred}>
                            {REJECTED_COPY.alsoFlagged(merged.deferred.map((x) => x.short || x.reason).join(', '))}
                        </div>
                    )}
                </div>
            ) : (
                <div className={s.infoLine}>{a.fix}</div>
            )}
            {cardRequestedAt && <AwaitStrip requestedAt={cardRequestedAt} />}
            {a.bucket === 'terminal' && !a.unmapped && (
                <div className={s.infoLine}>
                    {REJECTED_COPY.reference} <code className={s.refCode}>{opsRef(clientId, check.checkedAt)}</code>
                </div>
            )}
            {a.unmapped && <div className={`${s.infoLine} ${s.unmappedNotice}`}>{REJECTED_COPY.unmappedNotice}</div>}
            {extra}
            {/* PAYER_DOWN 是后端唯二不收费的拒识（另一个 SYSTEM_ERROR 有专属卡）：chip 换 no-charge 句 */}
            <BillingFooter
                persona={persona}
                skipCostModal={skipCostModal}
                freeText={a.code === 'PAYER_DOWN' ? FOOTER_COPY.payerDownNoCharge : undefined}
            />
        </>
    );
};

export default RejectedBody;
